import _ from 'lodash';
import { getActiveTab } from '@/store/modules/table/active-tab';
import { getTableQuery, getTableActiveReports } from '@/store/modules/table/accessors';
import { useStore } from '@/store';
import { reportTypeFromRoute } from '@/services/utils/reportType';
import {
  getDedicatedReportGroupBy,
  isDedicatedReportGroupBy,
  isDedicatedReportRoute,
} from '@/views/reports/report-route-behavior';
import {
  getActiveReportIndex,
  getTableQueryForCurrentTab,
  getTableQuerySearch,
  getTableColumnsFiltersForCurrentTabInQueryFormat,
} from '@/store/modules/table/query-getters';
import { getReportColumnPresetsState } from '@/store/modules/report-columns-accessors';
import { reportSearchRequestParams } from '@/store/reportColumns/report-search-params';

const getReportColumnsState = () => {
  const store = useStore();
  const columnPresets = getReportColumnPresetsState();

  return {
    dimensions: store.dimensions,
    metrics: store.metrics as string[],
    savedColumns: columnPresets.savedColumns,
    savedGroups: columnPresets.savedGroups,
    activePreset: columnPresets.activePreset,
    savedColumnsInCurentPreset: columnPresets.savedColumnsInCurentPreset,
  };
};

const getReportTypeFromReportUiRoute = (reportUiRoute: { type?: string; name?: string | symbol | null }) => {
  if (isDedicatedReportRoute(reportUiRoute.name)) {
    return getDedicatedReportGroupBy(reportUiRoute.name as string);
  }

  return reportUiRoute.type;
};

export const getSavedColumnsForCurrentTab = () => {
  const savedColumnsInCurentPreset = getReportColumnPresetsState().savedColumnsInCurentPreset;

  if (getActiveTab()) {
    const activeTabState = getTableActiveReports()[getActiveReportIndex()]?.state || {};
    return activeTabState.savedColumns || savedColumnsInCurentPreset;
  }

  return savedColumnsInCurentPreset;
};

export const getReportParamsByTabIdAndGroupBy = ({
  groupBy,
  tabId = getActiveTab(),
}: {
  groupBy: string;
  tabId?: number;
}) => {
  let tableQueryPath = getTableQuery();
  if (tabId > 0) {
    tableQueryPath = getTableActiveReports()?.find(item => item.tabId === tabId)?.state
      ?.tableQuery as typeof tableQueryPath;
  }
  if (!tableQueryPath) return;

  const reportSearchSetting = useStore().user?.reportSearchSetting;

  const tableQuerySearch = () => {
    if (tabId !== 0) {
      return tableQueryPath.search || {};
    }

    switch (reportSearchSetting) {
      case 'perTab':
        return getTableQuery()?.search?.[groupBy] || {};
      case 'allTabs':
      default:
        return getTableQuery().search || {};
    }
  };

  const searchState = tableQuerySearch() || {};
  const {
    currency,
    date,
    from,
    compareFrom,
    compareTo,
    compareDate,
    compareEnabled,
    limit,
    offset = {},
    status,
    timezone,
    to,
    view,
    formatResponse,
    filterHaving,
    filterHavingOptions,
    filterHavingValues,
  } = tableQueryPath as Record<string, unknown>;

  const tableQueryData = {
    currency,
    date,
    from,
    compareFrom,
    compareTo,
    compareDate,
    compareEnabled,
    limit,
    offset: (offset as Record<string, number>)[groupBy],
    status,
    timezone,
    to,
    view,
    formatResponse,
    filterHaving,
    filterHavingOptions,
    filterHavingValues,
  };

  switch (groupBy) {
    default:
      if (isDedicatedReportGroupBy(groupBy)) {
        return {
          ...tableQueryData,
          ...reportSearchRequestParams(searchState, { textExact: true }),
        };
      }

      return {
        ...tableQueryData,
        ...reportSearchRequestParams(searchState),
      };
  }
};

export const getReportParams = () => {
  const store = useStore();
  const { dimensions } = getReportColumnsState();
  const savedColumns = getSavedColumnsForCurrentTab();
  const groupBy = reportTypeFromRoute();
  const reportType = groupBy || getReportTypeFromReportUiRoute(store.report.route);
  const searchState = getTableQuerySearch();
  const searchParams = reportSearchRequestParams(searchState);
  const textSearchParams = searchParams.searchBy === 'text' ? searchParams : {};
  const { filterHaving, filterHavingOptions, filterHavingValues } =
    getTableColumnsFiltersForCurrentTabInQueryFormat();
  const {
    compareEnabled,
    compareDate,
    from,
    to,
    timezone,
    compareFrom,
    compareTo,
    date,
    currency,
    formatResponse = 1,
    limit,
    offset = {},
  } = getTableQueryForCurrentTab();
  const { status, postbackStatus } = getTableQuery();

  if (!savedColumns) return;

  const { columns: curColumns = [], order = {} } = savedColumns[reportType as string] || {};
  const { mainColumn } = (dimensions as Record<string, { mainColumn?: string }>)[reportType as string] || {};
  const columns = _.uniq([...(mainColumn ? [mainColumn] : []), ...curColumns]);

  const commonValue = {
    currency,
    date,
    from,
    compareFrom,
    compareTo,
    compareDate,
    compareEnabled,
    limit,
    offset: (offset as Record<string, number>)[groupBy as string],
    timezone,
    to,
    formatResponse,
    filterHaving,
    filterHavingOptions,
    filterHavingValues,
  };

  switch (reportType) {
    case 'conversions':
      return {
        ...commonValue,
        columns,
        groupBy: 'conversions',
        orderBy: order.orderBy || 'conversions-postbacktimestamp',
        orderDir: order.orderDir || 'desc',
        ...textSearchParams,
      };
    case 'postbacks':
      return {
        ...commonValue,
        columns,
        groupBy: 'postbacks',
        orderBy: order.orderBy || 'postbacks-timestamp',
        orderDir: order.orderDir || 'desc',
        ...(postbackStatus === 'all' ? {} : { postbackStatus }),
        ...textSearchParams,
      };
    case 'errors':
      return {
        ...commonValue,
        columns,
        groupBy: 'errors',
        orderBy: order.orderBy || 'errors-timestamp',
        orderDir: order.orderDir || 'desc',
        ...textSearchParams,
      };
    case 'live-visits':
      return {
        ...commonValue,
        columns,
        groupBy: 'live-visits',
        orderBy: order.orderBy || 'lives-timestamp',
        orderDir: order.orderDir || 'desc',
      };
    case 'live-clicks':
      return {
        ...commonValue,
        columns,
        groupBy: 'live-clicks',
        orderBy: order.orderBy || 'lives-timestamp',
        orderDir: order.orderDir || 'desc',
      };
    default:
      return {
        ...order,
        ...commonValue,
        columns,
        from,
        groupBy,
        status,
        ...searchParams,
      };
  }
};
